import Link from 'next/link'

import { Callout } from '@/app/components/Callout'
import { TrackingLocation } from '@/app/utils/analytics'
import { toPortableTextBlock } from '@/app/utils/portableText'
import { cn } from '@/app/utils/style'
import { buildDocumentPath } from '@/lang/urls'
import { Button, Callout as CalloutType } from '@/sanity.types'

type Guide = {
  _id: string
  _type?: string
  language: 'en' | 'es'
  title: string
  slug: string
  excerpt?: string | null
  featuredImage?: any
}

type CalloutGuideProps = {
  guide: Guide
  tracking: TrackingLocation
  showButton?: boolean
  buttonLabel?: string | null
  bgColor?: 'neutral-50' | 'neutral-100'
  showOverline?: boolean
  wrapInLink?: boolean
  className?: string
}

type DereferencedLink = {
  _type: 'link'
  linkType: 'internal'
  href: string
  label: string
}

type DereferencedButton = Omit<Button, 'link'> & {
  link: DereferencedLink
}

type DereferencedCallout = Omit<CalloutType, 'button'> & {
  button?: DereferencedButton
}

export function CalloutGuide({
  guide,
  tracking,
  showButton = false,
  buttonLabel,
  bgColor = 'neutral-50',
  showOverline = false,
  wrapInLink = false,
  className,
}: CalloutGuideProps) {
  const language = guide.language || 'en'
  const guideHref = buildDocumentPath({
    type: 'guide',
    slug: guide.slug,
    language,
  })
  const defaultButtonLabel = language === 'es' ? 'Leer guía' : 'Read guide'
  const overlineText = language === 'es' ? 'Guía' : 'Guide'

  const calloutData: DereferencedCallout = {
    _type: 'callout',
    ...(showOverline && { overline: overlineText }),
    alignment: 'left',
    heading: toPortableTextBlock(guide.title, `h-${guide._id}`) as any,
    text: guide.excerpt ? (toPortableTextBlock(guide.excerpt, `t-${guide._id}`) as any) : undefined,
    bgColor,
    showImage: !!guide.featuredImage,
    image: guide.featuredImage,
    showButton,
    ...(showButton && {
      button: {
        _type: 'button',
        size: 'sm',
        variant: 'tertiary',
        icon: {
          _type: 'icon',
          provider: 'fontawesome',
          style: 'solid',
          name: 'faAngleRight',
        },
        iconPlacement: 'right',
        link: {
          _type: 'link',
          linkType: 'internal',
          href: guideHref,
          label: buttonLabel || defaultButtonLabel,
        },
      },
    }),
  }

  if (wrapInLink) {
    return (
      <Link href={guideHref} className={cn('flex flex-col', className)}>
        <Callout
          callout={calloutData as any}
          tracking={tracking}
          className="flex-1"
          textClassName="line-clamp-3 pb-[3px]"
        />
      </Link>
    )
  }

  return (
    <Callout
      callout={calloutData as any}
      tracking={tracking}
      className={className}
      textClassName="line-clamp-3 pb-[3px]"
    />
  )
}
